import { defineStore } from 'pinia';
import { useUxStore } from "@/stores/uxStore";
import apiClient from "@/axiosConfig";

const STORAGE_LOCALE = "mkzugferd.locale";
const getUxStore = () => useUxStore();

function getCurrentLocale() {
    const persisted = localStorage.getItem(STORAGE_LOCALE);
    if (persisted && persisted.trim().length > 0) {
        return persisted.trim().toLowerCase();
    }

    const browserLocale = navigator.language || navigator.userLanguage || "de";
    return browserLocale.split("-")[0].toLowerCase();
}

export const useMasterDataStore = defineStore('masterDataStore', {
    state: () => ({
        countryCodes: [],
        selectedCountryCodes: [],
        currencyCodes: [],
        globalIdSchemeIdentifiers: [],
        invoiceParserTypes: [],
        invoiceTypes: [],
        quantityCodes: [],
        subjectCodes: [],
        taxCategoryCodes: [],
        taxRegistrationSchemeIds: [],
        XRechnungInvoiceTypes: [],
        ZUGFeRDProfiles: [],
        ZUGFeRDVersions: [],
    }),
    actions: {
        async fetchMasterData(endpoint, stateKey) {
            try {
                getUxStore().startLoading();
                const lang = getCurrentLocale();
                const response = await apiClient.get(endpoint, {params:{locale: lang}});
                this[stateKey] = response.data;
            } catch (error) {
                console.error(`Error in fetchMasterData(${endpoint}, ${stateKey}):`, error);
                if (error.response) {
                    getUxStore().setError(error.response.data);
                } else if (error.request) {
                    getUxStore().setError("No response from server. Please try again later.");
                } else {
                    getUxStore().setError("An unexpected error occurred: " + error.message);
                }
            } finally {
                getUxStore().stopLoading();
            }
        },

        //eigene Methode, weil ich die selectedCountryCodes passe und nur die entsprechenden Ländercodes zurückgeben möchte
        async fetchCurrencyCodes(locale) {
            try {
                getUxStore().startLoading();
                // Use passed locale instead of useI18n()
                const lang = locale || 'en'; // Default fallback
                const validCountryCodes = [...new Set(
                    this.selectedCountryCodes.filter(code => typeof code === 'string' && code.trim() !== '')
                )];

                const searchParams = new URLSearchParams({ locale: lang });
                validCountryCodes.forEach(code => searchParams.append('countryCodes', code));
                const url = `/MasterData/CurrencyCode?${searchParams.toString()}`;
                const response = await apiClient.get(url);
                this.currencyCodes = [...response.data];
            } catch (error) {
                console.error(`Error in fetchCurrencyCodes:`, error);
                if (error.response) {
                    getUxStore().setError(error.response.data);
                } else if (error.request) {
                    getUxStore().setError("No response from server. Please try again later.");
                } else {
                    getUxStore().setError("An unexpected error occurred: " + error.message);
                }
            } finally {
                getUxStore().stopLoading();
            }
        },
        async addCountryCode(newVal, oldVal = undefined) {
            const normalizedOldVal = typeof oldVal === 'string' ? oldVal.trim() : '';
            if (normalizedOldVal !== '') {
                const index = this.selectedCountryCodes.indexOf(normalizedOldVal);
                if (index > -1) {
                    this.selectedCountryCodes.splice(index, 1);
                }
            }

            const normalizedNewVal = typeof newVal === 'string' ? newVal.trim() : '';
            if (normalizedNewVal !== '' && !this.selectedCountryCodes.includes(normalizedNewVal)) {
                this.selectedCountryCodes.push(normalizedNewVal);
            }

            this.selectedCountryCodes = this.selectedCountryCodes.filter(code => typeof code === 'string' && code.trim() !== '');
            console.log("SELECTED COUNTRYCODES", this.selectedCountryCodes);

            // Get locale from the component
            //TODO FIX LANG SELECTION
            const locale = 'de';
            await this.fetchCurrencyCodes(locale);
        },
        clearSelectedCountryCodes() {
            this.selectedCountryCodes = [];
        },
        async fetchCountryCodes() {
            await this.fetchMasterData('/MasterData/CountryCode', 'countryCodes');
        },
        async fetchGlobalIdSchemeIdentifiers() {
            await this.fetchMasterData('/MasterData/GlobalIdSchemeIdentifier', 'globalIdSchemeIdentifiers');
        },
        async fetchInvoiceParserTypes() {
            await this.fetchMasterData('/MasterData/InvoiceParserType', 'invoiceParserTypes');
        },
        async fetchInvoiceTypes() {
            await this.fetchMasterData('/MasterData/InvoiceType', 'invoiceTypes');
        },
        async fetchQuantityCodes() {
            await this.fetchMasterData('/MasterData/QuantityCode', 'quantityCodes');
        },
        async fetchSubjectCodes() {
            await this.fetchMasterData('/MasterData/SubjectCode', 'subjectCodes');
        },
        async fetchTaxCategoryCodes() {
            await this.fetchMasterData('/MasterData/TaxCategoryCode', 'taxCategoryCodes');
        },
        async fetchTaxRegistrationSchemeIds() {
            await this.fetchMasterData('/MasterData/TaxRegistrationSchemeId', 'taxRegistrationSchemeIds');
        },
        async fetchXRechnungInvoiceType() {
            await this.fetchMasterData('/MasterData/XRechnungInvoiceType', 'XRechnungInvoiceTypes');
        },
        async fetchZUGFeRDProfiles() {
            await this.fetchMasterData('/MasterData/ZUGFeRDProfile', 'ZUGFeRDProfiles');
        },
        async fetchZUGFeRDVersions() {
            await this.fetchMasterData('/MasterData/ZUGFeRDVersion', 'ZUGFeRDVersions');
        },

        // Fetch all master data concurrently
        async fetchAllMasterData() {
            // Start a global loading indicator if you have one, or rely on individual calls
            getUxStore().startLoading();
            try {
                await Promise.all([
                    this.fetchCountryCodes(),
                    this.fetchCurrencyCodes(),
                    this.fetchGlobalIdSchemeIdentifiers(),
                    this.fetchInvoiceParserTypes(),
                    this.fetchInvoiceTypes(),
                    this.fetchQuantityCodes(),
                    this.fetchSubjectCodes(),
                    this.fetchTaxCategoryCodes(),
                    this.fetchTaxRegistrationSchemeIds(),
                    this.fetchXRechnungInvoiceType(),
                    this.fetchZUGFeRDProfiles(),
                    this.fetchZUGFeRDVersions(),
                ]);
            } catch (error) {
                // Any error handling if needed.
                // Individual fetch calls already handle errors, so this is optional.
                console.error("Error fetching all master data:", error);
            } finally {
                getUxStore().stopLoading();
            }
        },
    },
});
